import DOMPurify from "isomorphic-dompurify";
import { useId } from "react";
import { useFetcher } from "react-router";
import Button from "~/components/lib/Button";
import Input from "~/components/lib/Input";
import Text from "~/components/lib/Text";
import TextArea from "~/components/lib/TextArea";
import type { CreateBlogCommentActionResponse } from "~/entities/actions/types";
import { useReCaptcha } from "~/hooks/useReCaptcha";

const SUCCESS_MESSAGE =
  "Thank you for your comment. It will be posted once we approve it, which usually happens in a couple days.";

type Props = {
  parentId?: string;
};

const CommentForm: React.FC<Props> = ({ parentId }) => {
  const id = useId();
  const fetcher = useFetcher<CreateBlogCommentActionResponse>();
  const { getReCaptchaToken } = useReCaptcha();
  const response = fetcher.state === "idle" ? fetcher.data : undefined;
  const messages = response?.messages ?? [];

  const fieldError = (attribute: string) =>
    messages.find(
      (message) =>
        message.type === "field_error" && message.attribute === attribute,
    )?.text;
  const formError = messages.find((message) => message.type === "error")?.text;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    formData.set("recaptcha", await getReCaptchaToken("new_blog_comment"));
    fetcher.submit(formData, { method: "post" });
  };

  if (response?.ok) {
    return (
      <div role="status">
        <Text
          element="p"
          className="blog-comments__success"
        >
          {SUCCESS_MESSAGE}
        </Text>
      </div>
    );
  }

  return (
    <fetcher.Form
      method="post"
      onSubmit={handleSubmit}
      className="blog-comments__form"
    >
      <Text element="p">All fields marked with * are required</Text>
      {formError && (
        <div
          role="alert"
          className="blog-comments__form-error"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formError) }}
        />
      )}
      <TextArea
        id={`${id}-body`}
        name="body"
        label="Body*"
        required
        error={fieldError("body")}
      />
      <div className="blog-comments__inputs">
        <Input
          id={`${id}-name`}
          name="name"
          label="Name*"
          required
          error={fieldError("name")}
        />
        <Input
          id={`${id}-email`}
          name="email"
          type="email"
          label="Email*"
          required
          error={fieldError("email")}
        />
      </div>
      {parentId && (
        <input
          type="hidden"
          name="parentId"
          value={parentId}
        />
      )}
      <Button
        type="submit"
        disabled={fetcher.state !== "idle"}
        className="blog-comments__submit"
      >
        Post Comment
      </Button>
      {!parentId && (
        <Text element="p">
          We take your privacy seriously and will not share or publish your
          email address.
        </Text>
      )}
    </fetcher.Form>
  );
};

export default CommentForm;
